import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { LanguageService } from '../../core/services/language.service';
import { ExportService } from '../../core/services/export.service';
import { environment } from '../../../environments/environment';
import { LeaveService, WorkerLeaveSummary } from '../../../services/leave.service';

export interface AttendanceWorker {
  workerId: number;
  firstName: string;
  lastName: string;
  totalWorkingDays: number;
  pastWorkingDays: number;
  remainingWorkingDays: number;
  daysPresent: number;
  leaveDays: number;
  absentDays: number;
  salaryDeductionPercent: number;
}

export interface AttendanceReport {
  realEstateId: number;
  realEstateName: string;
  month: string;
  totalWorkingDays: number;
  pastWorkingDays: number;
  remainingWorkingDays: number;
  workers: AttendanceWorker[];
  totalPages?: number;
  totalElements?: number;
}

@Component({
  selector: 'app-attendance-report',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './attendance-report.component.html',
  styleUrls: ['./attendance-report.component.css']
})
export class AttendanceReportComponent implements OnInit {
  private isBrowser: boolean;

  // Data
  report: AttendanceReport | null = null;
  propertyId: number | null = null;

  // UI state
  isLoading = false;
  error: string | null = null;

  // Month selector
  selectedMonth: string = '';
  availableMonths: { value: string; label: string }[] = [];

  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  totalElements = 0;

  // Leave summary modal
  showLeaveSummaryModal = false;
  leaveSummary: WorkerLeaveSummary | null = null;
  isLoadingLeaveSummary = false;
  leaveSummaryError: string | null = null;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private languageService: LanguageService,
    private exportService: ExportService,
    private leaveService: LeaveService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  t(key: string): string {
    return this.languageService.translate(key);
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.propertyId = +id;
      this.generateAvailableMonths();
      this.loadReport();
    } else {
      this.error = 'ID de propriété non trouvé dans l\'URL.';
    }
  }

  private generateAvailableMonths(): void {
    const now = new Date();
    const months: { value: string; label: string }[] = [];
    const monthNames = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];

    // Generate months: current month and 11 months back
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const dayStr = '01';
      const monthStr = (date.getMonth() + 1).toString().padStart(2, '0');
      const yearStr = date.getFullYear().toString();
      const value = `${dayStr}-${monthStr}-${yearStr}`;
      const label = `${monthNames[date.getMonth()]} ${yearStr}`;
      months.push({ value, label });
    }

    this.availableMonths = months;
    this.selectedMonth = months[0].value; // Current month by default
  }

  onMonthChange(): void {
    this.currentPage = 1;
    this.loadReport();
  }

  loadReport(): void {
    if (!this.propertyId) return;

    this.isLoading = true;
    this.error = null;

    const url = environment.endpoints.attendance.report.replace('{propertyId}', this.propertyId.toString());
    const apiPage = this.currentPage - 1;
    const params: any = {
      page: apiPage,
      size: this.pageSize
    };
    if (this.selectedMonth) {
      params.month = this.selectedMonth;
    }

    const token = this.getAuthToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    this.http.get<AttendanceReport>(url, { headers, params }).subscribe({
      next: (data) => {
        console.log('✅ Rapport de présence chargé:', data);
        this.report = data;
        this.totalPages = data.totalPages || 1;
        this.totalElements = data.totalElements || data.workers?.length || 0;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('❌ Erreur chargement rapport de présence:', err);
        if (err.status === 404) {
          this.error = 'Aucun rapport trouvé pour cette période.';
        } else if (err.status === 403) {
          this.error = 'Accès refusé au rapport de présence.';
        } else {
          this.error = err.error?.message || 'Erreur lors du chargement du rapport de présence.';
        }
        this.isLoading = false;
      }
    });
  }

  private getAuthToken(): string {
    if (this.isBrowser) {
      return localStorage.getItem('token') || '';
    }
    return '';
  }

  // Badge color for salary deduction
  getDeductionBadgeClass(percent: number): string {
    if (percent < 25) {
      return 'bg-green-100 text-green-700 border-green-200';
    } else if (percent <= 50) {
      return 'bg-blue-100 text-blue-700 border-blue-200';
    } else {
      return 'bg-red-100 text-red-700 border-red-200';
    }
  }

  // Progress bar color
  getProgressBarColor(daysPresent: number, pastWorkingDays: number): string {
    if (pastWorkingDays === 0) return 'bg-gray-300';
    const ratio = daysPresent / pastWorkingDays;
    if (ratio >= 0.75) return 'bg-green-500';
    if (ratio >= 0.5) return 'bg-blue-500';
    return 'bg-red-500';
  }

  // Progress percentage
  getProgressPercent(daysPresent: number, pastWorkingDays: number): number {
    if (pastWorkingDays === 0) return 0;
    return Math.round((daysPresent / pastWorkingDays) * 100);
  }

  // Format month for display
  getFormattedMonth(): string {
    if (!this.report?.month) return '';
    const parts = this.report.month.split('-');
    if (parts.length !== 3) return this.report.month;
    const monthNames = [
      '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    const monthNum = parseInt(parts[1], 10);
    return `${monthNames[monthNum] || parts[1]} ${parts[2]}`;
  }

  // Export to Excel
  exportToExcel(): void {
    if (!this.report || !this.report.workers || this.report.workers.length === 0) {
      return;
    }

    const data = this.report.workers.map(w => ({
      'Nom': w.lastName,
      'Prénom': w.firstName,
      'Jours ouvrés totaux': w.totalWorkingDays,
      'Jours ouvrés passés': w.pastWorkingDays,
      'Jours restants': w.remainingWorkingDays,
      'Jours présents': w.daysPresent,
      'Jours de congé': w.leaveDays,
      'Jours absents': w.absentDays,
      'Déduction salaire (%)': w.salaryDeductionPercent,
      'Taux de présence (%)': this.getProgressPercent(w.daysPresent, w.pastWorkingDays)
    }));

    const filename = `Rapport_Presence_${this.report.realEstateName}_${this.report.month}`;
    this.exportService.exportToExcel(data, filename, 'Rapport de Présence');
  }

  // Retry loading
  retry(): void {
    this.loadReport();
  }

  // Leave summary modal
  openLeaveSummary(worker: AttendanceWorker): void {
    if (!this.propertyId) return;
    this.leaveSummary = null;
    this.leaveSummaryError = null;
    this.isLoadingLeaveSummary = true;
    this.showLeaveSummaryModal = true;
    if (this.isBrowser) document.body.style.overflow = 'hidden';

    this.leaveService.getWorkerSummary(this.propertyId, worker.workerId).subscribe({
      next: (data) => {
        this.leaveSummary = data;
        this.isLoadingLeaveSummary = false;
      },
      error: (err) => {
        this.leaveSummaryError = err.error?.message || 'Erreur lors du chargement du récapitulatif.';
        this.isLoadingLeaveSummary = false;
      }
    });
  }

  closeLeaveSummary(): void {
    this.showLeaveSummaryModal = false;
    this.leaveSummary = null;
    this.leaveSummaryError = null;
    if (this.isBrowser) document.body.style.overflow = '';
  }

  onLeaveSummaryBackdrop(event: Event): void {
    if (event.target === event.currentTarget) this.closeLeaveSummary();
  }

  formatLeaveDate(arr: number[]): string {
    return this.leaveService.formatDateArray(arr);
  }

  getLeaveStatusLabel(status: string): string {
    switch (status) {
      case 'APPROVED': return 'Approuvé';
      case 'REJECTED': return 'Refusé';
      default: return 'En attente';
    }
  }

  getLeaveStatusClass(status: string): string {
    switch (status) {
      case 'APPROVED': return 'bg-green-100 text-green-700 border-green-200';
      case 'REJECTED': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    }
  }

  // Pagination methods
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadReport();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadReport();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadReport();
    }
  }

  hasNextPage(): boolean {
    return this.currentPage < this.totalPages;
  }

  hasPreviousPage(): boolean {
    return this.currentPage > 1;
  }

  getPaginationText(): string {
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalElements);
    return `Voir ${start}-${end} sur ${this.totalElements}`;
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    if (this.totalPages <= maxPagesToShow) {
      for (let i = 1; i <= this.totalPages; i++) pages.push(i);
    } else {
      const halfRange = Math.floor(maxPagesToShow / 2);
      let startPage = Math.max(1, this.currentPage - halfRange);
      let endPage = Math.min(this.totalPages, this.currentPage + halfRange);
      if (endPage - startPage < maxPagesToShow - 1) {
        if (startPage === 1) endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);
        else startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      for (let i = startPage; i <= endPage; i++) pages.push(i);
    }
    return pages;
  }
}
